import React, {
  FC,
  Dispatch,
  Key,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react'
import { decode as decodeBase64 } from 'base-64'
import moment from 'moment'
import {
  Card,
  Col,
  Input,
  Radio,
  Select,
  Spin,
  Text,
  Title,
} from 'components/common'
import { SelectedVariant, VariantSelectorModalForm } from 'components/bookings'
import {
  OrderVariantInput,
  ProductAvailabilityInput,
  useProductAvailabilitiesLazyQuery,
  OrderPurpose,
  ProductAvailabilitiesQuery,
  ArenaBookingInfoInput,
  useProductsQuery,
  OrderLineItem,
  ProductStatus,
} from 'lib/graphql/storeadmin'
import {
  SAGA_ARENA_PRODUCT_NAME,
  SAGA_ARENA_PROFIT_TYPE,
  SAGA_ARENA_USAGE_TYPE,
  SUB_ARENA_BOOKING_TIMEZONE,
  SUB_ARENA_PROFIT_TYPE,
} from 'config'
import { FormInstance, RadioChangeEvent, Tooltip } from 'antd'
import { InfoCircleTwoTone } from '@ant-design/icons'
import { calculateMinutes, getDatesBetween } from 'helper'
import SagaArenaUsageTypeSelectCard from './SagaArenaUsageTypeSelectCard'
import ProductSelectCard from './ProductSelectCard'
import { EditFormType } from 'pages/admin/bookings/[id]/edit'

type SelectedProduct = {
  name: string
  variants: { id: string; name: string }[]
  baseTimeUnit: number
}

export type SagaArenaBookingTargetType = 'main' | 'sub' | 'other'

export type AncillaryFacilityOptions = {
  label: string
  value: string
  disabled?: boolean
}

export const getOrderPurposeString = (purpose: OrderPurpose) => {
  switch (purpose) {
    case 'CONCERT':
      return 'コンサート'
    case 'GENERAL':
      return '一般利用'
    case 'EVENT':
      return 'イベント利用'
    case 'OTHER':
      return 'その他'
    case 'SPORT':
      return 'スポーツ'
    default:
      return null
  }
}

export type AncillaryFacility = {
  variantId: string
  arenaBookingInfo: ArenaBookingInfoInput[]
}

type InputUsageInfo = {
  values: ArenaBookingInfoInput[]
  label: SAGA_ARENA_USAGE_TYPE
}

type SagaArenaProductSelectCardProps = {
  bookingDates: [moment.Moment, moment.Moment]
  initBookingDates?: [moment.Moment, moment.Moment]
  checkedKeys: React.Key[]
  hasCustomerId: boolean
  calculatePrices: (inputVariants: OrderVariantInput[]) => Promise<void>
  disabled: boolean
  sagaArenaEstimateNumber: {
    first: string
    second: string
  }
  setSagaArenaEstimateNumber: React.Dispatch<
    React.SetStateAction<{
      first: string
      second: string
    }>
  >
  form: FormInstance<VariantSelectorModalForm | EditFormType>
  handleDiffDates: () => Array<string>
  isEdit?: boolean
  isSagaArena: boolean
  lineItems?: OrderLineItem[]
  onCheck: (checkedKeysValue: Key[]) => void
  priceLoading: boolean
  purpose: OrderPurpose
  rateId: React.MutableRefObject<string>
  setCheckedKeys: React.Dispatch<React.SetStateAction<React.Key[]>>
  setPurpose: React.Dispatch<React.SetStateAction<OrderPurpose>>
  setSagaArenaSelectedVariants: React.Dispatch<
    React.SetStateAction<OrderVariantInput[]>
  >
  sagaArenaSelectedVariants: OrderVariantInput[]
  selectedVariants: SelectedVariant[]
  setSelectedVariants: React.Dispatch<React.SetStateAction<SelectedVariant[]>>
  specialPriceIncluded: boolean
  timezone: [moment.Moment, moment.Moment]
}
const SagaArenaProductSelectCard: FC<SagaArenaProductSelectCardProps> =
  React.memo((props) => {
    const {
      bookingDates,
      initBookingDates,
      hasCustomerId,
      checkedKeys,
      calculatePrices,
      disabled,
      form,
      handleDiffDates,
      isEdit,
      isSagaArena,
      lineItems,
      onCheck,
      priceLoading,
      purpose,
      rateId,
      setCheckedKeys,
      setPurpose,
      selectedVariants,
      setSelectedVariants,
      sagaArenaEstimateNumber,
      setSagaArenaEstimateNumber,
      setSagaArenaSelectedVariants,
      timezone,
    } = props

    const { data: productsData, loading: productsLoading } = useProductsQuery()
    const [
      getAvailability,
      { data: availableData, loading: availabilitiesLoading },
    ] = useProductAvailabilitiesLazyQuery({ fetchPolicy: 'network-only' })

    // 利用予定日数
    const [bookingDatesBetween, setBookingDatesBetween] =
      useState<string[]>(null)

    // 予約対象
    const [bookingTargetType, setBookingTargetType] =
      useState<SagaArenaBookingTargetType>('main')

    // 営利/非営利
    const [isForProfit, setIsForProfit] = useState(true)

    // 催事
    const [eventOfUsage, setEventOfUsage] = useState<InputUsageInfo>({
      values: [],
      label: SAGA_ARENA_USAGE_TYPE.EVENT,
    })

    // 休演
    const [noPerformance, setNoPerformance] = useState<InputUsageInfo>({
      values: [],
      label: SAGA_ARENA_USAGE_TYPE.NO_PERFORMANCE,
    })

    // 延長
    const [extension, setExtension] = useState<InputUsageInfo>({
      values: [],
      label: SAGA_ARENA_USAGE_TYPE.EXTENSION,
    })

    // 設営・撤去
    const [setupTearDown, setSetupTearDown] = useState<InputUsageInfo>({
      values: [],
      label: SAGA_ARENA_USAGE_TYPE.SETUP_TEARDOWN,
    })

    // 付属施設
    const [selectedAncillaryFacilities, setSelectedAncillaryFacilities] =
      useState<AncillaryFacility[]>([])

    /**
     * 予約可能施設データの更新
     */
    const updateAvailabilityData = useCallback(() => {
      const dates = handleDiffDates()
      const input: ProductAvailabilityInput = {
        dates,
        start: timezone[0].format('HH:mm'),
        end: timezone[1].format('HH:mm'),
      }
      if (rateId.current) {
        input.rateId = decodeBase64(rateId.current).split(':')[1]
      }
      // 予約可能施設を再取得
      getAvailability({
        variables: {
          input,
        },
      })
    }, [getAvailability, handleDiffDates, rateId, timezone])

    /**
     * 利用形態の数量を更新するための高階関数
     */
    const createUpdateQuantityFunction =
      (setStateFunc: Dispatch<SetStateAction<InputUsageInfo>>) =>
      (date: string, newQuantity: number) => {
        setStateFunc((prevState) => ({
          ...prevState, // labelは初期値で固定
          values: prevState.values.map((item) =>
            item.dateOfUse === date ? { ...item, quantity: newQuantity } : item
          ),
        }))
      }

    /**
     * 予約対象を変更する
     */
    const handleChangeBookingTarget = (e: RadioChangeEvent) => {
      // 料金計算中は予約対象の変更を禁止
      if (priceLoading) return
      setBookingTargetType(e.target.value as SagaArenaBookingTargetType)
    }

    /**
     * 特定の利用形態と日付に基づいてユーザーが入力した数量を取得する
     */
    const getQuantityByUsageTypeAndDate = (
      input: InputUsageInfo,
      date: string
    ): number | null => {
      // inputやinput.valuesが未定義の場合にはnullを返す
      if (!input || !input.values) return null

      return (
        input.values.find(({ dateOfUse }) => dateOfUse === date)?.quantity ??
        null
      )
    }

    /**
     * 施設名からアクティブな施設を検索する
     */
    const findActiveProductByName = useCallback(
      (productName: string) => {
        return productsData?.products?.edges
          .filter(({ product }) => product.status === ProductStatus.Active)
          .find(({ product }) => product.name.jaJp === productName)?.product
      },
      [productsData]
    )

    /**
     * 施設名から施設情報を取得する
     */
    const getProductByName = useCallback(
      (productName: string): SelectedProduct | null => {
        if (productsLoading || !productName) return null

        const findProduct = findActiveProductByName(productName)
        if (!findProduct) return null

        const product: SelectedProduct = {
          name: findProduct.name.jaJp,
          variants: findProduct.variants.edges.map(({ variant }) => ({
            id: variant.id,
            name: variant.title.jaJp,
          })),
          baseTimeUnit: findProduct.baseTimeUnit,
        }
        return product
      },
      [findActiveProductByName, productsLoading]
    )

    /**
     * サブアリーナの利用時間帯から「午前」「午後」「夜間」のいずれかを取得する
     */
    const getSubArenaBookingPeriod = useCallback((): string => {
      const start = timezone[0].format('HH:mm')
      const end = timezone[1].format('HH:mm')
      // 連結した文字列で比較
      switch (start + end) {
        case SUB_ARENA_BOOKING_TIMEZONE.MORNING.START +
          SUB_ARENA_BOOKING_TIMEZONE.MORNING.END:
          return SUB_ARENA_BOOKING_TIMEZONE.MORNING.NAME

        case SUB_ARENA_BOOKING_TIMEZONE.AFTERNOON.START +
          SUB_ARENA_BOOKING_TIMEZONE.AFTERNOON.END:
          return SUB_ARENA_BOOKING_TIMEZONE.AFTERNOON.NAME

        case SUB_ARENA_BOOKING_TIMEZONE.NIGHT.START +
          SUB_ARENA_BOOKING_TIMEZONE.NIGHT.END:
          return SUB_ARENA_BOOKING_TIMEZONE.NIGHT.NAME

        case SUB_ARENA_BOOKING_TIMEZONE.ALLDAY_08.START +
          SUB_ARENA_BOOKING_TIMEZONE.ALLDAY_08.END:
          return SUB_ARENA_BOOKING_TIMEZONE.ALLDAY_08.NAME

        case SUB_ARENA_BOOKING_TIMEZONE.ALLDAY_09.START +
          SUB_ARENA_BOOKING_TIMEZONE.ALLDAY_09.END:
          return SUB_ARENA_BOOKING_TIMEZONE.ALLDAY_09.NAME

        case SUB_ARENA_BOOKING_TIMEZONE.ALLDAY_10.START +
          SUB_ARENA_BOOKING_TIMEZONE.ALLDAY_10.END:
          return SUB_ARENA_BOOKING_TIMEZONE.ALLDAY_10.NAME

        default:
          return SUB_ARENA_BOOKING_TIMEZONE.MORNING.NAME
      }
    }, [timezone])

    /**
     * 施設情報・利用目的・利用形態・営利/非営利 から一致するバリエーションのIDを取得する
     */
    const getVariantId = useCallback(
      (
        product: SelectedProduct | null,
        purpose: OrderPurpose,
        mode: SAGA_ARENA_USAGE_TYPE,
        profitType?: SAGA_ARENA_PROFIT_TYPE
      ): string => {
        if (!product) return

        // バリエーション名取得のために必要な文字列を格納
        const conditions = [
          getOrderPurposeString(purpose),
          mode,
          profitType,
        ].filter(Boolean)

        // サブアリーナのみ利用時間帯に応じたバリエーションを追加する
        // 利用時間帯：午前、午後、夜間
        if (bookingTargetType === 'sub') {
          conditions.push(getSubArenaBookingPeriod())
        }

        // 配列の全ての文字列を含むバリエーション名を持つバリエーションを取得する
        return (
          product.variants.find((variant) =>
            conditions.every((condition) => variant.name.includes(condition))
          )?.id ?? null
        )
      },
      [bookingTargetType, getSubArenaBookingPeriod]
    )

    /**
     * 利用形態の状態を初期化する高階関数
     */
    const initializeUsageTypeState = useCallback(
      (setStateFunc: Dispatch<SetStateAction<InputUsageInfo>>) => {
        const initialState = bookingDatesBetween?.map((date) => ({
          dateOfUse: date,
          quantity: 0,
        }))
        // labelは固定なため、valueのみ更新する
        setStateFunc((prevState) => ({
          label: prevState.label,
          values: initialState ?? [],
        }))
      },
      [bookingDatesBetween]
    )

    /**
     * 利用形態ごとの状態を初期化する
     */
    const initializeUsageTypeStates = () => {
      initializeUsageTypeState(setNoPerformance)
      initializeUsageTypeState(setEventOfUsage)
      initializeUsageTypeState(setExtension)
      initializeUsageTypeState(setSetupTearDown)
    }

    // メインアリーナとサブアリーナを除く予約可能施設のリストとその選択肢のリストを生成
    const availableDataForArena: {
      facilities: ProductAvailabilitiesQuery
      options: AncillaryFacilityOptions[]
    } = useMemo(() => {
      // メインアリーナとサブアリーナを除いた施設の配列
      const filteredEdges = availableData?.productAvailabilities?.edges.filter(
        ({ product }) =>
          product.productName.jaJp !== SAGA_ARENA_PRODUCT_NAME.MAIN_ARENA &&
          product.productName.jaJp !== SAGA_ARENA_PRODUCT_NAME.SUB_ARENA
      )
      // 予約可能施設一覧
      const facilities = {
        productAvailabilities: {
          edges: filteredEdges,
        },
      }

      // 選択肢一覧
      const options: AncillaryFacilityOptions[] = filteredEdges
        ?.filter(({ product }) => product.variants.edges.length > 0)
        .map(({ product }) => {
          // 初期値と現在の日付が一致するかどうか
          let isEqualBookingDates = true
          if (isEdit) {
            if (bookingDates && initBookingDates) {
              isEqualBookingDates =
                initBookingDates[0].format('YYYY-MM-DD') ===
                  bookingDates[0].format('YYYY-MM-DD') &&
                initBookingDates[1].format('YYYY-MM-DD') ===
                  bookingDates[1].format('YYYY-MM-DD')
            }
          }
          return {
            label: product.productName.jaJp,
            value: product.variants.edges[0].variant.variantId,
            disabled:
              isEdit && isEqualBookingDates
                ? false
                : !product.variants.edges[0].variant.forSale,
          }
        })

      return {
        facilities,
        options,
      }
    }, [availableData, bookingDates, initBookingDates, isEdit])

    /**
     * バリエーションリストを作成する
     */
    const createVariants = useCallback(
      (
        inputUsageInfoItems: InputUsageInfo[],
        product: SelectedProduct,
        profitType = null
      ): OrderVariantInput[] => {
        return inputUsageInfoItems
          .filter((item) => item.values?.some(({ quantity }) => quantity > 0))
          .map((item) => ({
            variantId: getVariantId(product, purpose, item.label, profitType),
            arenaBookingInfo: item.values
              .filter(({ quantity }) => quantity > 0)
              .map((item) => ({
                dateOfUse: item.dateOfUse,
                quantity: item.quantity,
              })),
          }))
      },
      [getVariantId, purpose]
    )

    /**
     * 予約対象に基づいて料金計算及び予約作成のためのバリエーションリストを作成する
     */
    const getSagaArenaSelectedVariants = useCallback(
      (product: SelectedProduct): OrderVariantInput[] => {
        let variants: SelectedVariant[] = []
        let newVariants: OrderVariantInput[] = []
        const start = timezone[0].format('HH:mm')
        const end = timezone[1].format('HH:mm')
        const isVariant = (variants: OrderVariantInput[]) => {
          if (variants.length <= 0) return false
          return variants.every(({ variantId }) => variantId)
        }

        // 予約対象別にバリエーションリストの生成処理を分岐する
        switch (bookingTargetType) {
          case 'other':
            variants = selectedVariants?.map((variant) => variant) ?? []
            newVariants = variants.map((variant) => ({
              variantId: variant.variantId,
              arenaBookingInfo: bookingDatesBetween.map((date) => ({
                dateOfUse: date,
                quantity: Math.ceil(
                  calculateMinutes(start, end) / variant.baseTimeUnit
                ), //小数点以下切り上げ
              })),
            }))
            if (isVariant(newVariants)) calculatePrices(newVariants)
            return newVariants

          case 'main':
            newVariants = createVariants(
              [eventOfUsage, extension, setupTearDown, noPerformance],
              product,
              isForProfit
                ? SAGA_ARENA_PROFIT_TYPE.FOR_PROFIT
                : SAGA_ARENA_PROFIT_TYPE.NON_PROFIT
            )
            if (isVariant(newVariants)) calculatePrices(newVariants)
            return newVariants

          case 'sub':
            newVariants = createVariants(
              [eventOfUsage, setupTearDown, noPerformance],
              product,
              isForProfit
                ? SUB_ARENA_PROFIT_TYPE.FOR_PROFIT
                : SUB_ARENA_PROFIT_TYPE.NON_PROFIT
            )
            if (selectedAncillaryFacilities.length > 0) {
              selectedAncillaryFacilities.forEach((ancillaryFacility) =>
                newVariants.push(ancillaryFacility)
              )
            }
            if (isVariant(newVariants)) calculatePrices(newVariants)
            return newVariants
          default:
            return []
        }
      },
      [
        bookingDatesBetween,
        bookingTargetType,
        calculatePrices,
        createVariants,
        eventOfUsage,
        extension,
        isForProfit,
        noPerformance,
        selectedAncillaryFacilities,
        selectedVariants,
        setupTearDown,
        timezone,
      ]
    )

    // 付属施設の選択
    const handleChangeAncillaryFacility = (
      date: string,
      checkedValues: string[]
    ): void => {
      setSelectedAncillaryFacilities((prevState) => {
        let newFacilities: AncillaryFacility[] = [...prevState]

        // 各チェックボックスのIDに対してループ処理
        checkedValues.forEach((value) => {
          // ラベルから施設情報を取得する
          const selectedProduct = getProductByName(
            availableDataForArena.options.find(
              (option) => option.value === value
            ).label
          )

          // 料金発生単位を取得（未設定の場合は180に設定）
          const baseTimeUnit = selectedProduct.baseTimeUnit ?? 180

          // 新たな施設情報配列から現在のvariantIdに該当する施設情報を検索
          const existingFacility = newFacilities.find(
            (facility) => facility.variantId === value
          )

          // 利用時間 / 料金発生単位 から数量を計算する
          const quantity = usageTimeInMinutes / baseTimeUnit

          // 既存の施設が見つかった場合
          if (existingFacility) {
            // その施設に対して、同一日付の予約情報がすでに存在するかどうかをチェック
            if (
              !existingFacility.arenaBookingInfo.some(
                (info) => info.dateOfUse === date
              )
            ) {
              // 同一日付の予約情報が存在しない場合のみ新たな予約情報を追加
              existingFacility.arenaBookingInfo.push({
                dateOfUse: date,
                quantity,
              })
            }
          }
          // 既存の施設が見つからなかった場合、新たな施設情報を配列に追加する
          else {
            newFacilities.push({
              variantId: value,
              arenaBookingInfo: [{ dateOfUse: date, quantity }],
            })
          }
        })

        // 各施設のarenaBookingInfoから、今回指定した日付以外の予約情報を保持する
        newFacilities.forEach((facility) => {
          facility.arenaBookingInfo = facility.arenaBookingInfo.filter(
            (info) =>
              info.dateOfUse !== date ||
              checkedValues.includes(facility.variantId)
          )
        })

        // arenaBookingInfoが空になった施設を除去する
        newFacilities = newFacilities.filter(
          (facility) => facility.arenaBookingInfo.length > 0
        )

        return newFacilities
      })
    }

    // 分単位の利用時間
    const usageTimeInMinutes = useMemo(() => {
      if (!timezone) return
      return calculateMinutes(
        timezone[0].format('HH:mm'),
        timezone[1].format('HH:mm')
      )
    }, [timezone])

    // 施設名（メインアリーナ or サブアリーナ）
    const productName = useMemo(() => {
      switch (bookingTargetType) {
        case 'main':
          return SAGA_ARENA_PRODUCT_NAME.MAIN_ARENA
        case 'sub':
          return SAGA_ARENA_PRODUCT_NAME.SUB_ARENA
        default:
          return null
      }
    }, [bookingTargetType])

    // 予約対象の施設情報
    const selectedProduct = useMemo(() => {
      return getProductByName(productName)
    }, [getProductByName, productName])

    // 現在の予約情報からバリエーションを取得する
    const getVariant = (usageType: SAGA_ARENA_USAGE_TYPE) => {
      return lineItems.find(({ variantTitle }) =>
        variantTitle.jaJp.includes(usageType)
      )
    }

    /**
     * 予約情報から予約対象のタイプを返す
     */
    const getOrderProductTargetType = (): SagaArenaBookingTargetType => {
      // メインアリーナまたはサブアリーナの予約があるか調べるための配列
      const productNames = [
        SAGA_ARENA_PRODUCT_NAME.MAIN_ARENA,
        SAGA_ARENA_PRODUCT_NAME.SUB_ARENA,
      ]

      // メインアリーナまたはサブアリーナの予約があるか？
      const targetProduct =
        productNames.find((productName) =>
          lineItems.some(({ name }) => name.jaJp === productName)
        ) || SAGA_ARENA_PRODUCT_NAME.OTHER

      // 予約対象施設に応じて、bookingTargetTypeを返す
      switch (targetProduct) {
        case SAGA_ARENA_PRODUCT_NAME.MAIN_ARENA:
          return 'main'
        case SAGA_ARENA_PRODUCT_NAME.SUB_ARENA:
          return 'sub'
        default:
          return 'other'
      }
    }

    // 付属施設の予約情報
    const ancillaryFacilities = useMemo((): AncillaryFacility[] => {
      return lineItems
        ?.filter(
          ({ name }) =>
            name.jaJp !== SAGA_ARENA_PRODUCT_NAME.MAIN_ARENA &&
            name.jaJp !== SAGA_ARENA_PRODUCT_NAME.SUB_ARENA
        )
        ?.map((facility) => ({
          variantId: facility.variant,
          arenaBookingInfo: facility.arenaBookingInfo.map((item) => ({
            dateOfUse: item.dateOfUse,
            quantity: item.quantity,
          })),
        }))
    }, [lineItems])

    /**
     * 現在のSAGAアリーナの予約情報と利用形態に基づいて更新された予約情報オブジェクトを生成する
     */
    const generateUpdatedArenaBookingInfo = (
      prevState: InputUsageInfo,
      variantType: SAGA_ARENA_USAGE_TYPE
    ): InputUsageInfo => {
      // バリエーションに基づく現在の予約情報を取得
      const currentBookingInfo = getVariant(variantType)?.arenaBookingInfo ?? []

      // 取得した数量情報に基づいて、新たな予約情報を生成
      const updatedBookingInfo = bookingDatesBetween?.reduce(
        (newBookingInfo, date) => {
          // 既存のSAGAアリーナ予約情報に対象の日付が存在するかをチェック
          const foundDate = newBookingInfo.find(
            (info) => info.dateOfUse === date
          )

          // 既存のSAGAアリーナ予約情報に対象の日付が存在しない場合、その日付の予約情報を追加
          if (!foundDate) {
            newBookingInfo.push({
              dateOfUse: date,
              quantity: 0,
              id: currentBookingInfo[0]?.id,
            })
          }
          return newBookingInfo
        },
        [...currentBookingInfo] // 現在の予約情報のコピーを初期値として使用
      )
      // 生成した新たな予約情報を含むオブジェクトを生成
      return {
        ...prevState,
        values: updatedBookingInfo,
      }
    }

    /**
     * 予約編集時の初期値をセットする
     */
    const initializeStatesOnEdit = () => {
      if (!initBookingDates || !bookingDates) return

      const isEqualBookingDates =
        initBookingDates[0].format('YYYY-MM-DD') ===
          bookingDates[0].format('YYYY-MM-DD') &&
        initBookingDates[1].format('YYYY-MM-DD') ===
          bookingDates[1].format('YYYY-MM-DD')
      if (!isEqualBookingDates) return

      // =========================
      // 初期値の生成
      // =========================
      const orderProductTargetType = getOrderProductTargetType()
      const profitType = lineItems?.some(({ variantTitle }) =>
        variantTitle.jaJp
          .split(' / ')
          .some((title) => title === SAGA_ARENA_PROFIT_TYPE.FOR_PROFIT)
      )
      const variantIds = lineItems.map(({ variant }) => variant).filter(Boolean)
      // =========================
      // 初期値のセット
      // =========================
      // 利用目的
      setPurpose(purpose)
      // 営利/非営利
      setIsForProfit(profitType)
      // 予約対象の選択状態
      setBookingTargetType(orderProductTargetType)
      // 設営・撤去
      setSetupTearDown((prevState) =>
        generateUpdatedArenaBookingInfo(
          prevState,
          SAGA_ARENA_USAGE_TYPE.SETUP_TEARDOWN
        )
      )
      // 休演
      setNoPerformance((prevState) =>
        generateUpdatedArenaBookingInfo(
          prevState,
          SAGA_ARENA_USAGE_TYPE.NO_PERFORMANCE
        )
      )
      // 延長
      setExtension((prevState) =>
        generateUpdatedArenaBookingInfo(
          prevState,
          SAGA_ARENA_USAGE_TYPE.EXTENSION
        )
      )
      // 催事
      setEventOfUsage((prevState) =>
        generateUpdatedArenaBookingInfo(prevState, SAGA_ARENA_USAGE_TYPE.EVENT)
      )
      // サブアリーナの付属施設の選択状態をセットする
      setSelectedAncillaryFacilities(ancillaryFacilities)
      // その他の施設の選択状態をセットする
      setCheckedKeys(variantIds)
    }

    // 予約詳細が更新されたら料金計算及び予約作成に使用するバリエーションリストを更新する
    useEffect(() => {
      if (!bookingDates || !timezone || !hasCustomerId) return
      setSagaArenaSelectedVariants(
        getSagaArenaSelectedVariants(selectedProduct)
      )
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      bookingTargetType,
      bookingDates,
      hasCustomerId,
      eventOfUsage,
      extension,
      isForProfit,
      noPerformance,
      purpose,
      setupTearDown,
      selectedAncillaryFacilities,
      selectedVariants,
      timezone,
    ])

    // 利用予定日から開始日から終了日までの全ての日付を取得して、利用予定日の日数を更新する
    useEffect(() => {
      if (bookingDates?.length > 0) {
        // 開始日と終了日のフォーマットを整形
        const start = bookingDates[0].format('YYYY-MM-DD')
        const end = bookingDates[1].format('YYYY-MM-DD')
        // 開始日～終了日までのすべての日付を取得
        const dates = getDatesBetween(start, end)
        // stateを更新
        setBookingDatesBetween(dates)
      }
    }, [bookingDates])

    // 利用予定日、時間、予約対象が変更されたら予約詳細をリセットする
    useEffect(() => {
      form.resetFields()
      setCheckedKeys([])
      setSelectedAncillaryFacilities([])
      setPurpose(null)
      setIsForProfit(true)
      setSetupTearDown({
        values: [],
        label: SAGA_ARENA_USAGE_TYPE.SETUP_TEARDOWN,
      })
      setEventOfUsage({ values: [], label: SAGA_ARENA_USAGE_TYPE.EVENT })
      setNoPerformance({
        values: [],
        label: SAGA_ARENA_USAGE_TYPE.NO_PERFORMANCE,
      })
      setExtension({ values: [], label: SAGA_ARENA_USAGE_TYPE.EXTENSION })
      setSagaArenaSelectedVariants([])

      // 利用予定日が選択されていたら利用形態ごとに初期値をセットする
      if (bookingDatesBetween?.length > 0) initializeUsageTypeStates()

      // 予約編集時
      if (isEdit) initializeStatesOnEdit()
      else if (isSagaArena)
        setSagaArenaEstimateNumber({ first: '', second: '' })

      // 予約対象がその他の時は予約可能施設のリストを取得する
      if (
        bookingTargetType === 'sub' ||
        bookingTargetType === 'other' ||
        (bookingTargetType === 'main' && bookingDates && timezone)
      ) {
        updateAvailabilityData()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookingDatesBetween, timezone, bookingTargetType])

    // 入力された利用日時から予約可能かどうかを判定する
    const canOrder = useMemo((): boolean => {
      // 編集時または予約対象がその他なら予約可能
      if (isEdit || bookingTargetType === 'other') return true

      // 予約対象の施設名
      const targetProductName =
        bookingTargetType === 'main'
          ? SAGA_ARENA_PRODUCT_NAME.MAIN_ARENA
          : SAGA_ARENA_PRODUCT_NAME.SUB_ARENA

      // 予約対象のedgeを取得
      const targetVariantEdge =
        availableData?.productAvailabilities?.edges.find(
          ({ product }) => product.productName.jaJp === targetProductName
        )
      // 予約対象のedgeが存在しない場合は予約不可
      if (!targetVariantEdge) return false

      // 予約対象のedgeにforSaleがfalseのものが存在する場合は予約不可
      const hasItemNotForSale = targetVariantEdge.product.variants.edges.some(
        ({ variant }) => !variant.forSale
      )
      if (hasItemNotForSale) return false

      return true
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableData, bookingTargetType, isEdit, bookingDates, timezone])

    return (
      <div>
        <Card shadow>
          <Col span={24}>
            <Title level={5}>予約対象</Title>
          </Col>
          <Radio.Group
            value={bookingTargetType}
            onChange={handleChangeBookingTarget}
            disabled={priceLoading || disabled || isEdit}
          >
            <Radio value="main">メインアリーナ</Radio>
            <Radio value="sub">サブアリーナ</Radio>
            <Radio value="other">その他</Radio>
          </Radio.Group>
        </Card>

        <Card shadow hidden={disabled} style={{ position: 'relative' }}>
          {/* データが取得できていて、予約できないときに表示 */}
          {availableData && !canOrder && (
            <p className="font-bold text-2xl m-0 text-rose-500 mb-8">
              選択された利用日時は既に予約が作成されています。
            </p>
          )}
          <Title level={5}>予約詳細</Title>
          <div className="grid grid-cols-1 gap-y-10">
            {bookingTargetType === 'main' && (
              <div>
                <Col span={24} className="mb-2">
                  <div className="flex items-center space-x-1">
                    <Text strong>見積番号</Text>
                    <Tooltip title="番号がわかる場合は入力してください">
                      <InfoCircleTwoTone />
                    </Tooltip>
                  </div>
                </Col>
                <div className="w-1/2 grid grid-cols-12 items-stretch space-x-3">
                  <div className="col-span-8 flex flex-col">
                    <Input
                      type="text"
                      value={sagaArenaEstimateNumber.first}
                      onChange={(e) =>
                        setSagaArenaEstimateNumber((prev) => ({
                          ...prev,
                          first: e.target.value,
                        }))
                      }
                      placeholder="E0000000"
                      minLength={8}
                      disabled={!canOrder}
                    />
                  </div>
                  <div className="col-span-1 flex items-center justify-center">
                    <div className="bg-black h-0.5 w-full" />
                  </div>
                  <div className="col-span-3 flex flex-col">
                    <Input
                      type="text"
                      placeholder="01"
                      minLength={2}
                      value={sagaArenaEstimateNumber.second}
                      onChange={(e) =>
                        setSagaArenaEstimateNumber((prev) => ({
                          ...prev,
                          second: e.target.value,
                        }))
                      }
                      disabled={!canOrder}
                    />
                  </div>
                </div>
              </div>
            )}

            <div>
              <Col span={24} className="mb-2">
                <Text strong>利用目的</Text>
              </Col>
              <div className="grid grid-cols-3 gap-x-16">
                <Select
                  value={purpose}
                  onChange={(e) => setPurpose(e)}
                  disabled={!canOrder}
                >
                  {bookingTargetType === 'other' ? (
                    <Select.Option value={OrderPurpose.General}>
                      一般利用
                    </Select.Option>
                  ) : (
                    <>
                      {bookingTargetType === 'main' && (
                        <Select.Option value={OrderPurpose.Concert}>
                          コンサート
                        </Select.Option>
                      )}
                      <Select.Option value={OrderPurpose.Sport}>
                        スポーツ
                      </Select.Option>
                      <Select.Option value={OrderPurpose.Other}>
                        MICE・その他
                      </Select.Option>
                    </>
                  )}
                </Select>
              </div>
            </div>

            {(bookingTargetType === 'main' || bookingTargetType === 'sub') && (
              <div>
                <Col span={24} className="mb-2">
                  <Text strong>営利 / 非営利</Text>
                </Col>
                <div className="grid grid-cols-3 gap-x-16">
                  <Radio.Group
                    value={isForProfit}
                    onChange={(e) => setIsForProfit(e.target.value)}
                    disabled={!canOrder}
                  >
                    <Radio value={true}>営利</Radio>
                    <Radio value={false}>非営利</Radio>
                  </Radio.Group>
                </div>
              </div>
            )}

            {(bookingTargetType === 'main' || bookingTargetType === 'sub') && (
              <div>
                <Col span={24} className="mb-2 space-x-2">
                  <Text strong>利用形態</Text>
                  {!purpose && (
                    <span className="opacity-80">
                      ※利用目的を選択してください
                    </span>
                  )}
                </Col>
                <Spin
                  size="large"
                  spinning={priceLoading || availabilitiesLoading}
                  tip="Loading..."
                >
                  {bookingDatesBetween?.length > 0 && (
                    <ul className="grid gap-y-6">
                      {bookingDatesBetween.map((date) => (
                        <SagaArenaUsageTypeSelectCard
                          availableData={availableDataForArena}
                          eventOfUsageValue={
                            getQuantityByUsageTypeAndDate(eventOfUsage, date) >
                            0
                          }
                          extensionValue={getQuantityByUsageTypeAndDate(
                            extension,
                            date
                          )}
                          date={date}
                          handleChangeAncillaryFacility={
                            handleChangeAncillaryFacility
                          }
                          isPurposeSelected={!!purpose}
                          key={date}
                          noPerformanceValue={
                            getQuantityByUsageTypeAndDate(noPerformance, date) >
                            0
                          }
                          priceLoading={priceLoading}
                          setupTeardownValue={getQuantityByUsageTypeAndDate(
                            setupTearDown,
                            date
                          )}
                          selectedAncillaryFacilities={
                            selectedAncillaryFacilities
                          }
                          updateEventOfUsageQuantity={createUpdateQuantityFunction(
                            setEventOfUsage
                          )}
                          updateSetupTeardownQuantity={createUpdateQuantityFunction(
                            setSetupTearDown
                          )}
                          updateNoPerformanceQuantity={createUpdateQuantityFunction(
                            setNoPerformance
                          )}
                          updateExtensionQuantity={createUpdateQuantityFunction(
                            setExtension
                          )}
                          type={bookingTargetType}
                          canOrder={canOrder}
                        />
                      ))}
                    </ul>
                  )}
                </Spin>
              </div>
            )}
            {bookingTargetType === 'other' && (
              <ProductSelectCard
                availableDataForSagaArena={availableDataForArena?.facilities}
                bookingDates={bookingDates}
                checkedKeys={checkedKeys}
                hasCustomerId={hasCustomerId}
                calculatePrices={calculatePrices}
                disabled={disabled}
                form={form}
                handleDiffDates={handleDiffDates}
                lineItems={lineItems}
                onCheck={onCheck}
                rateId={rateId}
                selectedVariants={selectedVariants}
                setSelectedVariants={setSelectedVariants}
                timezone={timezone}
                isSagaArena={isSagaArena}
              />
            )}
          </div>
        </Card>
      </div>
    )
  })

export default SagaArenaProductSelectCard
